import squel from 'squel';
import { storedSyncErrorIsValidation } from './validation-issues.sql';
import SQLDatabase from './sql.service';

const getAncestorRecordIds = async (recordId: number): Promise<number[]> => {
  const rows = await SQLDatabase.instance.query<{ id: number }>(
    `WITH RECURSIVE ancestors(id) AS (
      SELECT a.sourceId AS id
      FROM associations a
      INNER JOIN attributes attr
        ON attr.id = a.attributeId
        AND attr.projectId = a.projectId
        AND attr.questionType = 'child'
      WHERE a.targetId = ?
      UNION
      SELECT a.sourceId AS id
      FROM associations a
      INNER JOIN attributes attr
        ON attr.id = a.attributeId
        AND attr.projectId = a.projectId
        AND attr.questionType = 'child'
      INNER JOIN ancestors ancestor ON ancestor.id = a.targetId
    )
    SELECT DISTINCT id FROM ancestors`,
    [recordId]
  );
  return rows.map(row => row.id);
};

const childAssociationJoin = `
  INNER JOIN attributes attr
    ON attr.id = a.attributeId
    AND attr.projectId = a.projectId
    AND attr.questionType = 'child'
`;

const getRootIdsWithDescendantValidationError = async (recordIds: number[]): Promise<Set<number>> => {
  if (recordIds.length === 0) {
    return new Set();
  }

  const placeholders = recordIds.map(() => '?').join(', ');
  const rows = await SQLDatabase.instance.query<{ rootId: number }>(
    `WITH RECURSIVE descendants(rootId, id) AS (
      SELECT a.sourceId AS rootId, a.targetId AS id
      FROM associations a
      ${childAssociationJoin}
      WHERE a.sourceId IN (${placeholders})
      UNION
      SELECT d.rootId, a.targetId AS id
      FROM associations a
      ${childAssociationJoin}
      INNER JOIN descendants d ON d.id = a.sourceId
    )
    SELECT DISTINCT d.rootId AS rootId
    FROM descendants d
    INNER JOIN records r ON r.id = d.id
    WHERE ${storedSyncErrorIsValidation('r')}`,
    recordIds
  );
  return new Set(rows.map(row => row.rootId));
};

export const recomputeTreeSyncErrorsForRecordIds = async (recordIds: number[]): Promise<void> => {
  const uniqueRecordIds = [...new Set(recordIds)];
  if (uniqueRecordIds.length === 0) {
    return;
  }

  const rootsWithDescendantError = await getRootIdsWithDescendantValidationError(uniqueRecordIds);
  const operations = uniqueRecordIds.map(recordId => {
    const statement = squel.update()
      .table('records')
      .where('id = ?', recordId)
      .set('treeSyncError', rootsWithDescendantError.has(recordId) ? 1 : null);
    const { text, values } = statement.toParam();
    return {
      statement: text,
      values
    };
  });
  await SQLDatabase.instance.executeSet(operations);
};

export const recomputeTreeSyncErrorsForAncestors = async (recordId: number): Promise<void> => {
  const ancestorIds = await getAncestorRecordIds(recordId);
  await recomputeTreeSyncErrorsForRecordIds(ancestorIds);
};

/** Recompute treeSyncError for the record and all ancestors (after direct syncError changes). */
export const recomputeTreeSyncErrorsForRecord = async (recordId: number): Promise<void> => {
  const ancestorIds = await getAncestorRecordIds(recordId);
  await recomputeTreeSyncErrorsForRecordIds([recordId, ...ancestorIds]);
};
